"use strict";

exports.__esModule = true;
exports.default = exports.TextArea = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));
var _Tooltip = _interopRequireDefault(require("../Tooltip/Tooltip"));
var _excluded = ["id", "name", "value", "label", "helper", "floatLabel", "maxChar", "resizable", "readOnly", "disabled", "required", "addon", "addonPosition", "placeholder", "onChange", "onInput", "onBlur", "onError", "className", "customValidation", "booleanValidation", "customInvalidation", "customMessage", "withErrorTooltip"];
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * The TextArea component allows an user to enter multiline data into the application.
 */
var TextArea = exports.TextArea = function TextArea(props) {
  var controlledId = props.id,
    name = props.name,
    controlledValue = props.value,
    initialLabel = props.label,
    helper = props.helper,
    floatLabel = props.floatLabel,
    maxChar = props.maxChar,
    resizable = props.resizable,
    readOnly = props.readOnly,
    disabled = props.disabled,
    required = props.required,
    addon = props.addon,
    addonPosition = props.addonPosition,
    placeholder = props.placeholder,
    onChange = props.onChange,
    onInput = props.onInput,
    onBlur = props.onBlur,
    onError = props.onError,
    className = props.className,
    customValidation = props.customValidation,
    booleanValidation = props.booleanValidation,
    customInvalidation = props.customInvalidation,
    customMessage = props.customMessage,
    withErrorTooltip = props.withErrorTooltip,
    attributes = _objectWithoutPropertiesLoose(props, _excluded);
  var textareaRef = _react.default.useRef(null);
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var _React$useState = _react.default.useState(controlledValue || ''),
    value = _React$useState[0],
    setValue = _React$useState[1];
  var _React$useState2 = _react.default.useState([0, 0]),
    caretPosition = _React$useState2[0],
    setCaretPosition = _React$useState2[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(value, {
      required: required,
      maxChar: maxChar,
      customInvalidation: customInvalidation,
      customValidation: customValidation,
      customMessage: customMessage,
      booleanValidation: booleanValidation
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  var _useLabel = (0, _useLabel2.default)(initialLabel, _extends({}, props, {
      id: id,
      value: value
    })),
    label = _useLabel.label;
  _react.default.useEffect(function () {
    return setValue(controlledValue || '');
  }, [controlledValue]);
  _react.default.useEffect(function () {
    textareaRef.current.setSelectionRange(caretPosition[0], caretPosition[1]);
  }, [value, caretPosition]);
  _react.default.useEffect(function () {
    if (typeof onError === 'function') {
      onError({
        name: textareaRef.current.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors]);
  var handleChange = function handleChange(event) {
    event.persist();
    setCaretPosition([event.target.selectionStart, event.target.selectionEnd]);
    setValue(event.target.value);
    methods.onChange(event.target.value);
    if (typeof onChange === 'function') {
      onChange(textareaRef.current.value, textareaRef.current.name);
    }
  };
  var handleBlur = function handleBlur() {
    methods.onChange(textareaRef.current.value);
    if (typeof onBlur === 'function') {
      onBlur(textareaRef.current.value, textareaRef.current.name);
    }
  };
  var inputAddon = /*#__PURE__*/_react.default.isValidElement(addon) ? addon : /*#__PURE__*/_react.default.createElement('span', {}, addon);
  var containerClasses = (0, _clsx.default)('uk-form-item', 'uk-textarea', addon && 'has-info', addon && "has-info--" + addonPosition, helper.length > 0 && 'has-helper', (errors.length > 0 || disabled || readOnly) && 'is-frozen', errors.length > 0 && 'has-error', errors.length > 0 && withErrorTooltip && 'has-error--tooltip', disabled && 'disabled', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container"
  }, /*#__PURE__*/_react.default.createElement("textarea", _extends({
    ref: textareaRef,
    id: id,
    className: (0, _clsx.default)('uk-inputtable', resizable && 'is-resizable'),
    placeholder: placeholder && !floatLabel ? placeholder : null,
    name: name || id,
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    onChange: handleChange,
    onBlur: handleBlur,
    value: value,
    "aria-describedby": (name || id) + "-helper-text"
  }, attributes)), maxChar && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-textarea__counter"
  }, "Characters left: ", maxChar - value.length > 0 ? maxChar - value.length : 0), addon && /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _clsx.default)('uk-textarea__addon', addonPosition)
  }, inputAddon), errors.length > 0 && withErrorTooltip && /*#__PURE__*/_react.default.createElement(_Tooltip.default, {
    trigger: "!",
    className: "uk-form-item__error-tooltip"
  }, /*#__PURE__*/_react.default.createElement("div", {
    id: (name || id) + "-errors-text"
  }, errors))), errors.length > 0 && !withErrorTooltip && /*#__PURE__*/_react.default.createElement("span", {
    className: "uk-form-item__error",
    id: (name || id) + "-errors-text"
  }, errors), errors.length > 0 && !withErrorTooltip ? '' : helper.length > 0 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, helper));
};
TextArea.propTypes = {
  /**
   * ID of the TextArea (used to bind the label to the TextArea)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the TextArea (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Default value of the TextArea
   */
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Label of the TextArea
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * TextArea placeholder
   */
  placeholder: _propTypes.default.string,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Remove the placeholder (if any), replace it with a floating label
   */
  floatLabel: _propTypes.default.bool,
  /**
   * Enable the resizing of the TextArea
   */
  resizable: _propTypes.default.bool,
  /**
   * Set and display a maximun of characters the user can input before triggering an error
   */
  maxChar: _propTypes.default.number,
  /**
   * Set the TextArea as read-only
   */
  readOnly: _propTypes.default.bool,
  /**
   * Set the TextArea as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the TextArea as required
   */
  required: _propTypes.default.bool,
  /**
   * Add an informative box next to the input element
   */
  addon: _propTypes.default.string,
  /**
   * Position the info relative to the input element
   */
  addonPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onInput
   */
  onInput: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Display error in tooltip otherwise display error below input.
   */
  withErrorTooltip: _propTypes.default.bool,
  /**
   * Add a custom boolean validation
   */
  booleanValidation: _propTypes.default.bool
};
TextArea.defaultProps = {
  placeholder: '',
  helper: '',
  floatLabel: false,
  resizable: true,
  readOnly: false,
  disabled: false,
  required: false,
  addonPosition: 'right',
  withErrorTooltip: true
};
var _default = exports.default = TextArea;